"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Plus, Trash2 } from "lucide-react";
import { SlideUpPage } from "@/components/layout/SlideUpPage";

interface CreateJobFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

const JOB_TYPES = [
  { value: "full-time", label: "Heltid" },
  { value: "part-time", label: "Deltid" },
  { value: "contract", label: "Vikariat" },
  { value: "internship", label: "Prosjekt" },
];

const REMOTE_OPTIONS = [
  { value: "onsite", label: "På kontoret" },
  { value: "hybrid", label: "Hybrid" },
  { value: "remote", label: "Fjernarbeid" },
];

export function CreateJobForm({ onClose, onSuccess }: CreateJobFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    location: "",
    jobType: "full-time" as const,
    remoteOption: "onsite" as const,
    description: "",
    requirements: [""],
    tags: [] as string[],
    salaryMin: "",
    salaryMax: "",
    showSalary: false,
  });

  const addRequirement = () => {
    setFormData({ ...formData, requirements: [...formData.requirements, ""] });
  };

  const removeRequirement = (index: number) => {
    setFormData({
      ...formData,
      requirements: formData.requirements.filter((_, i) => i !== index),
    });
  };

  const updateRequirement = (index: number, value: string) => {
    const newReqs = [...formData.requirements];
    newReqs[index] = value;
    setFormData({ ...formData, requirements: newReqs });
  };

  const addTag = (tag: string) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData({ ...formData, tags: [...formData.tags, tag] });
    }
  };

  const removeTag = (tag: string) => {
    setFormData({ ...formData, tags: formData.tags.filter((t) => t !== tag) });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    const validRequirements = formData.requirements.filter((r) => r.trim());
    if (validRequirements.length === 0) {
      setError("Legg til minst ett krav");
      return;
    }

    setIsSubmitting(true);

    try {
      // TODO: Replace with actual Convex mutation
      // const jobData = {
      //   title: formData.title,
      //   location: formData.location,
      //   jobType: formData.jobType,
      //   remoteOption: formData.remoteOption,
      //   description: formData.description,
      //   requirements: validRequirements,
      //   tags: formData.tags,
      //   salaryRange: formData.showSalary && formData.salaryMin && formData.salaryMax
      //     ? {
      //         min: parseInt(formData.salaryMin),
      //         max: parseInt(formData.salaryMax),
      //         currency: "NOK",
      //         period: "annual",
      //       }
      //     : undefined,
      // };
      // await createJob(jobData);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      console.log("Job created:", formData);
      onSuccess();
    } catch (err: any) {
      setError(err.message || "Kunne ikke opprette stilling");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SlideUpPage onClose={onClose} showCloseButton={true}>
      <div className="min-h-full p-6" style={{ backgroundColor: "var(--color-bg-primary)" }}>
        {/* Header */}
        <div className="mb-6 pt-10">
          <h2
            style={{
              fontSize: "var(--font-size-title-md)",
              fontWeight: "var(--font-weight-semibold)",
              color: "var(--color-text-primary)",
            }}
          >
            Legg til stilling
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Stillingstittel */}
          <div>
            <label
              htmlFor="title"
              className="mb-1.5 block text-sm font-medium"
              style={{ color: "var(--color-text-primary)" }}
            >
              Stillingstittel *
            </label>
            <input
              type="text"
              id="title"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full rounded-lg border px-4 py-2.5"
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                borderColor: "var(--color-border)",
                color: "var(--color-text-primary)",
              }}
              placeholder="F.eks. Senior Frontend Utvikler"
            />
          </div>

          {/* Lokasjon og Type */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="location"
                className="mb-1.5 block text-sm font-medium"
                style={{ color: "var(--color-text-primary)" }}
              >
                Lokasjon *
              </label>
              <input
                type="text"
                id="location"
                required
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full rounded-lg border px-4 py-2.5"
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text-primary)",
                }}
                placeholder="Oslo, Bergen, etc."
              />
            </div>

            <div>
              <label
                htmlFor="jobType"
                className="mb-1.5 block text-sm font-medium"
                style={{ color: "var(--color-text-primary)" }}
              >
                Type *
              </label>
              <select
                id="jobType"
                required
                value={formData.jobType}
                onChange={(e) =>
                  setFormData({ ...formData, jobType: e.target.value as any })
                }
                className="w-full rounded-lg border px-4 py-2.5"
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text-primary)",
                }}
              >
                {JOB_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Remote Option */}
          <div>
            <label
              htmlFor="remoteOption"
              className="mb-1.5 block text-sm font-medium"
              style={{ color: "var(--color-text-primary)" }}
            >
              Arbeidsmåte *
            </label>
            <select
              id="remoteOption"
              required
              value={formData.remoteOption}
              onChange={(e) =>
                setFormData({ ...formData, remoteOption: e.target.value as any })
              }
              className="w-full rounded-lg border px-4 py-2.5"
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                borderColor: "var(--color-border)",
                color: "var(--color-text-primary)",
              }}
            >
              {REMOTE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Beskrivelse */}
          <div>
            <label
              htmlFor="description"
              className="mb-1.5 block text-sm font-medium"
              style={{ color: "var(--color-text-primary)" }}
              >
              Stillingsbeskrivelse *
            </label>
            <textarea
              id="description"
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={5}
              className="w-full rounded-lg border px-4 py-2.5"
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                borderColor: "var(--color-border)",
                color: "var(--color-text-primary)",
              }}
              placeholder="Beskriv stillingen..."
            />
          </div>

          {/* Krav */}
          <div>
            <label
              className="mb-1.5 block text-sm font-medium"
              style={{ color: "var(--color-text-primary)" }}
            >
              Krav/kvalifikasjoner *
            </label>
            {formData.requirements.map((req, index) => (
              <div key={index} className="mb-2 flex gap-2">
                <input
                  type="text"
                  value={req}
                  onChange={(e) => updateRequirement(index, e.target.value)}
                  className="flex-1 rounded-lg border px-4 py-2.5"
                  style={{
                    backgroundColor: "var(--color-bg-secondary)",
                    borderColor: "var(--color-border)",
                    color: "var(--color-text-primary)",
                  }}
                  placeholder={`Krav ${index + 1}`}
                />
                {formData.requirements.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRequirement(index)}
                    className="rounded-lg px-3 transition-colors hover:bg-red-900/20"
                  >
                    <Trash2 className="h-5 w-5" style={{ color: "#ef4444" }} />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addRequirement}
              className="mt-2 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                color: "var(--color-text-primary)",
              }}
            >
              <Plus className="h-4 w-4" />
              Legg til krav
            </button>
          </div>

          {/* Lønn (valgfritt) */}
          <div className="space-y-3 rounded-lg border p-4" style={{ borderColor: "var(--color-border)" }}>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showSalary"
                checked={formData.showSalary}
                onChange={(e) => setFormData({ ...formData, showSalary: e.target.checked })}
                className="h-4 w-4 rounded"
              />
              <label
                htmlFor="showSalary"
                className="text-sm font-medium"
                style={{ color: "var(--color-text-primary)" }}
              >
                Vis lønn (valgfritt)
              </label>
            </div>

            {formData.showSalary && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="salaryMin"
                    className="mb-1.5 block text-sm"
                    style={{ color: "var(--color-text-secondary)" }}
                  >
                    Fra (NOK)
                  </label>
                  <input
                    type="number"
                    id="salaryMin"
                    value={formData.salaryMin}
                    onChange={(e) => setFormData({ ...formData, salaryMin: e.target.value })}
                    className="w-full rounded-lg border px-4 py-2.5"
                    style={{
                      backgroundColor: "var(--color-bg-secondary)",
                      borderColor: "var(--color-border)",
                      color: "var(--color-text-primary)",
                    }}
                    placeholder="600000"
                  />
                </div>
                <div>
                  <label
                    htmlFor="salaryMax"
                    className="mb-1.5 block text-sm"
                    style={{ color: "var(--color-text-secondary)" }}
                  >
                    Til (NOK)
                  </label>
                  <input
                    type="number"
                    id="salaryMax"
                    value={formData.salaryMax}
                    onChange={(e) => setFormData({ ...formData, salaryMax: e.target.value })}
                    className="w-full rounded-lg border px-4 py-2.5"
                    style={{
                      backgroundColor: "var(--color-bg-secondary)",
                      borderColor: "var(--color-border)",
                      color: "var(--color-text-primary)",
                    }}
                    placeholder="800000"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Tags (valgfritt) */}
          <div>
            <label
              htmlFor="newTag"
              className="mb-1.5 block text-sm font-medium"
              style={{ color: "var(--color-text-primary)" }}
            >
              Tags (valgfritt)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                id="newTag"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addTag((e.target as HTMLInputElement).value);
                    (e.target as HTMLInputElement).value = "";
                  }
                }}
                className="flex-1 rounded-lg border px-4 py-2.5"
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text-primary)",
                }}
                placeholder="Skriv tag og trykk Enter"
              />
            </div>
            {formData.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="flex items-center gap-1 rounded-full px-3 py-1 text-sm"
                    style={{
                      backgroundColor: "var(--color-bg-accent)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-1 hover:opacity-70"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div
              className="rounded-lg p-3 text-sm"
              style={{
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                color: "#ef4444",
              }}
            >
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 rounded-lg py-3 font-semibold"
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                color: "var(--color-text-primary)",
              }}
            >
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 rounded-lg py-3 font-semibold"
              style={{
                backgroundColor: "var(--color-bg-accent)",
                color: "var(--color-text-primary)",
              }}
            >
              {isSubmitting ? "Oppretter..." : "Opprett stilling"}
            </Button>
          </div>
        </form>
      </div>
    </SlideUpPage>
  );
}

